import type { ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { UserOutlined } from '@ant-design/icons'
import { Card } from 'antd'
import { CopyToClipboardButton } from 'uikit/buttons/copy-to-clipboard'
import { selectPlatform } from 'store/global'
import type { EntityDetails } from 'store/verticals/types'
import { isUrl } from '../../utils/utils'
import styles from './identity-details.module.scss'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </div>
  )
}

function TextValue({ value }: { value: string }) {
  return <span className={styles.fieldValue}>{value}</span>
}

function CopyableListItem({ value }: { value: string }) {
  const isLink = isUrl(value)
  return (
    <div className={styles.listRow}>
      {isLink ? (
        <a href={value} target='_blank' rel='noopener noreferrer' className={styles.listLink}>
          {value}
        </a>
      ) : (
        <span className={styles.listValue}>{value}</span>
      )}
      <CopyToClipboardButton text={value} />
    </div>
  )
}

export interface IdentityDetailsProps {
  entityDetails: EntityDetails
}

function formatBirthday(raw: string): string {
  const date = new Date(raw)
  if (isNaN(date.getTime())) return raw
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function IdentityDetails({ entityDetails }: IdentityDetailsProps) {
  const phones = entityDetails.phones || []
  const emails = entityDetails.emails || []
  const urls = entityDetails.profileUrls || []
  const platform = useSelector(selectPlatform)

  const isOnPrem = platform === 'prem'
  const hasImage = !!entityDetails.image
  const showImage = !isOnPrem && hasImage

  return (
    <div className={styles.container}>
      <div className={styles.avatarWrapper}>
        {showImage ? (
          <img src={entityDetails.image} alt={entityDetails.name || 'Identity'} className={styles.avatar} />
        ) : (
          <div className={styles.avatarPlaceholder}>
            <UserOutlined />
          </div>
        )}
      </div>

      <Field label='Display Name'>
        <TextValue value={entityDetails.name || 'N/A'} />
      </Field>

      {entityDetails.birthday && (
        <Field label='Birthday'>
          <TextValue value={formatBirthday(entityDetails.birthday)} />
        </Field>
      )}

      {phones.length > 0 && (
        <Field label='Phones'>
          <div className={styles.listItems}>
            {phones.map(phone => (
              <CopyableListItem key={phone} value={phone} />
            ))}
          </div>
        </Field>
      )}

      {emails.length > 0 && (
        <Field label='Emails'>
          <div className={styles.listItems}>
            {emails.map(email => (
              <CopyableListItem key={email} value={email} />
            ))}
          </div>
        </Field>
      )}

      {urls.length > 0 && (
        <Field label='URLs'>
          <div className={styles.listItems}>
            {urls.map(url => (
              <CopyableListItem key={url} value={url} />
            ))}
          </div>
        </Field>
      )}

      {entityDetails.location && (
        <Field label='Location'>
          <Card size='small'>
            <span className={styles.fieldValue}>{entityDetails.location}</span>
          </Card>
        </Field>
      )}
    </div>
  )
}
